/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { QRCodeComponent, useLocalization } from "@lemmonhq/uikit";
import { getRewardText } from "@lemmonhq/orders";
import type { CollectedProgram } from "@app/server/client";
import type { MembershipWalletPass } from "@app/server/trpc/routers/wallet-passes";
import { useCustomer } from "../../providers/auth-context";
import { WalletCard } from "./WalletCard";
import { membershipCardBindings } from "./passDesign";

/**
 * A membership pass, drawn by the very same WalletCard the coin cards use.
 *
 * This is an adapter, not a second card: a membership has no shop, balance or
 * tiers, so it is shaped into the CollectedProgram WalletCard expects, with the
 * membership's own design and bindings. Everything printed on it comes from
 * that design.
 */

export type MembershipWalletCardProps = {
  pass: MembershipWalletPass;
  /** Drop the width cap and fill the container — the stack owns its own width. */
  wide?: boolean;
  /** Render the scanner-sized back face instead of the compact front preview. */
  largeQr?: boolean;
  onSelect?: () => void;
  /**
   * Header text when the design's logo text is empty. Defaults to "Membership
   * card"; the place fan passes the programme name.
   */
  eyebrow?: string;
};

export const MembershipWalletCard = defineComponent<MembershipWalletCardProps>({
  name: "MembershipWalletCard",
  props: ["pass", "wide", "largeQr", "onSelect", "eyebrow"],
  setup(props) {
    const { t } = useLocalization();
    const { customer } = useCustomer();

    /** The membership shaped into the coin card's model. */
    const program = computed<CollectedProgram>(() => ({
      coin_program_id: props.pass.program_id,
      name: props.pass.program_name,
      coin_label: "",
      coin_type: "",
      icon: null,
      balance: 0,
      earned: 0,
      redeemed: 0,
      next_expiry: props.pass.expires_at,
      tiers: [],
      design: props.pass.design,
    }));

    const bindings = computed(() =>
      membershipCardBindings(
        props.pass,
        props.pass.reward ? getRewardText(props.pass.reward, t) : null,
      ),
    );

    /** LoyaltyModal recognises this prefix and resolves the global profile. */
    const loyaltyQrValue = computed(() =>
      customer.value?.id ? `lemmon-loyalty:${customer.value.id}` : null,
    );

    return () => (
      <WalletCard
        kind="membership"
        program={program.value}
        design={props.pass.design}
        bindings={bindings.value}
        shopName={props.eyebrow ?? t("customer.collected.show_card")}
        wide={props.wide}
        iconOverride="card_membership"
        // The back swaps the small panel code for the till-sized one.
        qrValue={props.largeQr ? null : undefined}
        back={props.largeQr}
        onSelect={props.onSelect}
        center={() =>
          props.largeQr && loyaltyQrValue.value ? (
            <div class="w-full max-w-[12rem] rounded-xl bg-white p-3 shadow-lg">
              <div class="aspect-square w-full">
                <QRCodeComponent url={loyaltyQrValue.value} />
              </div>
            </div>
          ) : (
            <span
              class="material-symbols-outlined text-6xl opacity-90"
              aria-hidden="true"
            >
              card_membership
            </span>
          )
        }
      />
    );
  },
});
